import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import * as React from 'react'

import { Logger } from 'src/3rds/cube'
import { ViewSlot } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import MuiDrawer from '@mui/material/Drawer'
import Toolbar from '@mui/material/Toolbar'

import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import MenuIcon from '@mui/icons-material/Menu'
import ApplicationLogo from 'src/components/icons/ApplicationLogo'
import LiaLogo from 'src/components/icons/LiaLogo'

import { useContainer } from 'src/utils/views/useContainer'

import Link from '@mui/material/Link'
import { FCClassContext, classToFComponent } from 'src/3rds/cube-react'

import { MainScope } from '../Main.scopes'
import { AlertView } from './alert.view'

import DrawerContent from './components/DrawerContent'
import NotificationIcon from './components/NotificationIcon'
import SnackAlert from './components/SnackAlert'
import UserInfoMenuButton from './components/UserInfoMenuButton'

const LOG = Logger.get('MainView')

const drawerWidth = 240

export const useStyles = makeStyles()((theme) => ({
    view: {
        display: 'flex',
        flexGrow: 1,
        flexShrink: 1,
        width: '100vw'
    },
    contentBody: {
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        flexGrow: 1,
        backgroundColor: 'rgb(247, 249, 252)'
    },
    appBarMenuButton: {
        marginLeft: -5,
        marginRight: 0
    },
    logoButton: {
        display: 'flex',
        flexDirection: 'row',
        alignSelf: 'center',
        alignItems: 'center',
        marginLeft: 10
    },
    chevronIcon: {
        color: 'white'
    },
    logoIcon: {
        width: '5.5em',
        height: '2em'
    },
    grabSpace: {
        flexGrow: 1,
        display: 'flex',
        flexBasis: 0,
        alignSelf: 'stretch'
    },
    toolbar: {},
    divider: {
        borderColor: '#ffffff36'
    },
    main: {
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
        flexGrow: 1,
        marginTop: '64px',
        overflow: 'auto',
        display: 'flex'
    },
    body: {
        flexGrow: 1
    },
    dialog: {
        '& .MuiDialog-paper': {
            maxWidth: 900
        }
    }
}))

interface AppBarProps extends MuiAppBarProps {
    open?: boolean
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    marginLeft: '12px',
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    })
}))

/////////////////////

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    '& .MuiDrawer-paper': {
        position: 'relative',
        backgroundColor: '#000',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        }),
        boxSizing: 'border-box',
        ...(!open && {
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen
            }),
            width: theme.spacing(7),
            [theme.breakpoints.up('sm')]: {
                width: theme.spacing(9)
            }
        })
    }
}))

const DrawerToolbar = styled(Toolbar)({
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'stretch',
    paddingLeft: '20px !important',
    paddingRight: '0px !important',
    justifyContent: 'space-between'
})

const AppToolbar = styled(Toolbar)({
    display: 'flex',
    gap: 10
})

const TitleDivider = styled(Divider)({
    borderRightWidth: 'medium',
    borderColor: '#333333a6',
    marginRight: 5
})

///////////////////

export type MainViewProps = {
    className?: string
    scope: MainScope
}

class MainViewClass implements FCClassContext<MainViewProps> {
    scope!: MainScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState({ scope }: MainViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className }: MainViewProps) {
        const { scope, classes } = this

        LOG.debug('scope.drawerOpen=' + scope.drawerOpen)

        const container = useContainer()

        let content: React.ReactNode = <></>

        if (scope.authenticated) {
            content = (
                <>
                    <CssBaseline />
                    <AppBar position="absolute" open={scope.drawerOpen}>
                        <AppToolbar>
                            <IconButton
                                edge="start"
                                className={classes.appBarMenuButton}
                                color="inherit"
                                aria-label="menu"
                                onClick={this.emitDrawerToggle}
                            >
                                <MenuIcon />
                            </IconButton>
                            {!scope.drawerOpen && (
                                <Link className={classes.logoButton} component="button" variant="body2">
                                    <ApplicationLogo className={classes.logoIcon} place="app-bar" textColor="white" />
                                    <LiaLogo size="small" textColor="white" sx={{ marginLeft: 1 }} />
                                </Link>
                            )}
                            <div className={classes.grabSpace}>
                                <ViewSlot className={classes.toolbar} scope={scope.toolbar} optional={true} />
                            </div>
                            <NotificationIcon count={2} />

                            <TitleDivider orientation="vertical" variant="middle" flexItem />

                            <UserInfoMenuButton mainScope={scope} />
                        </AppToolbar>
                    </AppBar>
                    <Drawer variant="permanent" container={container} open={scope.drawerOpen}>
                        <DrawerToolbar>
                            <Link
                                className={classes.logoButton}
                                component="button"
                                variant="body2"
                            >
                                <ApplicationLogo className={classes.logoIcon} place="app-bar" textColor="white" />
                                <LiaLogo size="small" textColor="white" sx={{ marginLeft: 1 }} />
                            </Link>
                            <IconButton onClick={this.emitDrawerToggle} className={classes.chevronIcon}>
                                <ChevronLeftIcon />
                            </IconButton>
                        </DrawerToolbar>

                        <Divider />
                        <DrawerContent mainScope={scope} />
                    </Drawer>
                    <ViewSlot className={classes.main} scope={scope.body} optional={false} />
                </>
            )
        } else {
            content = <ViewSlot className={classes.contentBody} scope={scope.body} optional={false} />
        }

        return (
            <Box className={clsx(classes.view, className)}>
                {content}

                <Dialog
                    className={classes.dialog}
                    open={!!scope.dialog}
                    onClose={this.emitCloseDialog}
                    aria-labelledby="form-dialog-title"
                >
                    <ViewSlot scope={scope.dialog} />
                </Dialog>

                <Dialog open={!!scope.alert} onClose={this.emitCloseAlert} aria-labelledby="form-dialog-title">
                    <ViewSlot scope={scope.alert} view={AlertView} />
                </Dialog>

                <SnackAlert scope={scope} />
            </Box>
        )
    }

    // Emissors

    emitDrawerToggle = () => {
        this.scope.onDrawerToggle().catch(LOG.caught)
    }

    emitCloseDialog = () => {
        this.scope.dialog?.onClose().catch(LOG.caught)
    }

    emitCloseAlert = () => {
        this.scope.alert?.onClose().catch(LOG.caught)
    }
}

export const MainView = classToFComponent(MainViewClass)
